import { useEffect, useState, useContext } from 'react'
import I18n from 'i18n-js'
import { useHistory } from 'react-router-dom'
import {
  Box,
  Grid,
  Typography,
  Hidden,
  IconButton,
  Switch,
  Chip,
  CircularProgress,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { SearchBar, CrudsBtn, SnackBar, AlertMenu } from '../../components'
import medicalStaff from '../../services/medicalStaff'
import {
  listCoordinatingStaff,
  deleteCoordinatingStaff,
} from '../../services/coordinatingStaff'
import { AuthContext } from '../../context/auth'
import { canAccess } from '../../router/permissions'
import { actionKeys, images, pagesKeys } from '../../utils'
import { Icons } from '../../utils'
import useStyles from '../crudsStyles'

const CoordinatingStaff = () => {
  const classes = useStyles()

  //state
  const { state } = useContext(AuthContext)
  const history = useHistory()
  const [loading, setLoading] = useState(true)

  //permissions
  const pageId = pagesKeys.coordinatingStaff

  //search
  const [search, setSearch] = useState('')

  //list
  const [data, setData] = useState([])

  //show alert
  const [show, setShow] = useState(false)
  const [message, setMessage] = useState({
    text: '',
    variant: 'success',
  })

  //delete
  const [staffDelete, setStaffDelete] = useState()
  const [anchor, setAnchor] = useState()

  const getData = async () => {
    setLoading(true)
    const res = await listCoordinatingStaff()
    const raw = res?.data?.coordinatingStaff ?? res?.data?.data ?? res?.data
    const list = Array.isArray(raw)
      ? raw
      : raw && typeof raw === 'object'
      ? Object.values(raw)
      : []
    setData(
      list.map((item) => ({
        ...item,
        status: item.status ? true : false,
      }))
    )
    setLoading(false)
  }

  const handleChangeSearchbar = (e) => {
    setSearch(e.target.value)
  }

  // change status
  const changeStatus = async (id, status) => {
    const json = await medicalStaff.changeStatus(id, { status: !status })
    if (json.data) {
      setData([
        ...data.map((item) => {
          if (item.id == id)
            return {
              ...item,
              status: !item.status,
            }
          return item
        }),
      ])
      setMessage({
        text: 'coordinatingStaff.statusChanged',
        variant: 'success',
      })
    } else {
      setMessage({
        text: 'components.errorStatus',
        variant: 'warning',
      })
    }
    setShow(true)
  }

  // remove
  const handleDeleteOpen = (e) => {
    setAnchor(e.currentTarget)
  }
  const handleDeleteClose = () => {
    setAnchor(null)
  }
  const remove = async () => {
    handleDeleteClose()

    const json = await deleteCoordinatingStaff(staffDelete)

    if (json.data) {
      setData([...data.filter((item) => item.id !== staffDelete)])
      setMessage({
        text: 'coordinatingStaff.deleted',
        variant: 'success',
      })
    } else {
      setMessage({
        text: 'coordinatingStaff.error',
        variant: 'warning',
      })
    }

    setShow(true)

    setSearch('')

    const searchInput = document.getElementById('search')
    if (searchInput) searchInput.value = ''
  }

  useEffect(() => {
    if (!canAccess(actionKeys.view, pageId, state)) {
      history.push('/dashboard')
    } else {
      getData()
    }
  }, [])

  // Table
  const columns = [
    {
      field: 'fullName',
      headerName: I18n.t('coordinatingStaff.tableheader.fullName'),
      flex: 1,
    },
    {
      field: 'email',
      headerName: I18n.t('coordinatingStaff.tableheader.email'),
      flex: 1,
    },
    {
      field: 'phone',
      headerName: I18n.t('coordinatingStaff.tableheader.phone'),
      flex: 1,
    },
    {
      field: 'centers',
      headerName: I18n.t('coordinatingStaff.tableheader.centers'),
      flex: 1.5,
      sortable: false,
      // eslint-disable-next-line react/display-name
      renderCell: ({ row }) =>
        row.centerNames.length ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px',
              py: '4px',
            }}
          >
            {row.centerNames.map((name, index) => (
              <Chip key={index} size="small" label={name} />
            ))}
          </Box>
        ) : (
          I18n.t('general.dataNotFound')
        ),
    },
    {
      field: 'status',
      headerName: I18n.t('coordinatingStaff.tableheader.status'),
      align: 'center',
      headerAlign: 'center',
      width: 120,
      sortable: false,
      // eslint-disable-next-line react/display-name
      renderCell: ({ row }) =>
        canAccess(actionKeys.edit, pageId, state) ? (
          <Switch
            color="primary"
            size="small"
            checked={row.status}
            onChange={() => changeStatus(row.id, row.status)}
          />
        ) : (
          <img src={row.status ? images.Active : images.Inactive} />
        ),
    },
    {
      field: 'actions',
      headerName: I18n.t('general.actions'),
      headerAlign: 'center',
      width: 170,
      sortable: false,
      hide:
        canAccess(actionKeys.detail, pageId, state) ||
        canAccess(actionKeys.edit, pageId, state) ||
        canAccess(actionKeys.delete, pageId, state)
          ? false
          : true,
      // eslint-disable-next-line react/display-name
      renderCell: ({ row }) => (
        <Grid container justifyContent="center">
          {canAccess(actionKeys.detail, pageId, state) && (
            <IconButton
              onClick={() =>
                history.push(`${history.location.pathname}/${row.id}`)
              }
              color="secondary"
            >
              {Icons('description')}
            </IconButton>
          )}
          {canAccess(actionKeys.edit, pageId, state) && (
            <IconButton
              onClick={() =>
                history.push(`${history.location.pathname}/${row.id}/edit`)
              }
              color="secondary"
            >
              {Icons('edit')}
            </IconButton>
          )}
          {canAccess(actionKeys.delete, pageId, state) && (
            <IconButton
              onClick={(e) => {
                setStaffDelete(row.id)
                handleDeleteOpen(e)
              }}
            >
              {Icons('delete')}
            </IconButton>
          )}
        </Grid>
      ),
    },
  ]

  const rows = data.map((item) => ({
    id: item.id,
    fullName: `${item.name || ''} ${item.lastname || ''}`.trim(),
    email: item.email || '',
    phone: item.phone || '',
    centerNames: (item.medical_centers || []).map((center) => center.name),
    status: item.status,
  }))

  const filterData = (rows) => {
    const term = search?.toLowerCase() ?? ''
    return (
      rows?.filter(
        (item) =>
          item.fullName.toLowerCase().includes(term) ||
          item.email.toLowerCase().includes(term) ||
          item.phone.toLowerCase().includes(term) ||
          item.centerNames.join(' ').toLowerCase().includes(term)
      ) ?? []
    )
  }

  return (
    <Box p={4}>
      <Grid
        container
        spacing={2}
        direction="column"
        justifyContent="center"
        alignItems="center"
        alignContent="center"
        wrap="nowrap"
      >
        <Grid item className={classes.items}>
          <Typography variant="body2" color="textSecondary">
            {I18n.t('breadcrumb.coordinatingStaff')} /{' '}
            {I18n.t('breadcrumb.dashboard')}
          </Typography>
        </Grid>
        <Grid item className={classes.itemFlex}>
          <Typography variant="h5" color="primary" className={classes.title}>
            {I18n.t('coordinatingStaff.title')}
          </Typography>
          {canAccess(actionKeys.list, pageId, state) && (
            <Hidden smDown>
              <div className={classes.search}>
                <SearchBar change={handleChangeSearchbar} />
              </div>
            </Hidden>
          )}

          {canAccess(actionKeys.create, pageId, state) && (
            <CrudsBtn
              text={I18n.t('coordinatingStaff.add')}
              size="medium"
              click={() => history.push(`${history.location.pathname}/create`)}
            />
          )}
        </Grid>
        {canAccess(actionKeys.list, pageId, state) && (
          <Hidden mdUp>
            <Grid item className={classes.items}>
              <SearchBar change={handleChangeSearchbar} />
            </Grid>
          </Hidden>
        )}
        {canAccess(actionKeys.list, pageId, state) && (
          <Grid item className={classes.items}>
            <div style={{ width: '100%', background: 'white' }}>
              {!loading ? (
                <DataGrid
                  columns={columns}
                  rows={filterData(rows)}
                  autoHeight
                  pagination
                  pageSize={10}
                  rowsPerPageOptions={[10]}
                  disableColumnMenu
                  disableSelectionOnClick
                  getRowHeight={() => 'auto'}
                  className={classes.datagrid}
                />
              ) : (
                <CircularProgress className={classes.circularProgress} />
              )}
            </div>
          </Grid>
        )}
      </Grid>
      <SnackBar
        text={I18n.t(message.text)}
        severity={message.variant}
        show={show}
        HandleShow={setShow}
        time={1500}
      />
      <AlertMenu anchor={anchor} close={handleDeleteClose} action={remove} />
    </Box>
  )
}

export default CoordinatingStaff
